import { useQuery } from '@tanstack/react-query';
import { fetchMyPermissions } from '@/api/permissions';
import type {
  AllPermissions,
  PermissionResource,
  ResourcePermissions,
} from '@/lib/constants/permissions';
import { IAM_PERMISSIONS } from '@/lib/constants/permissions';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';

const options = {
  queryKey: ['permissions', 'me'],
  queryFn: fetchMyPermissions,
  staleTime: 5 * 60_000,
};

/**
 * One resource's actions as flags, off the catalog rather than a list kept
 * here: `IAM_PERMISSIONS.orders.read` is `'orders:read'`, the same literal the
 * backend's route guards are built from.
 */
function flagsFor<R extends PermissionResource>(
  granted: Set<string>,
  resource: R,
): ResourcePermissions<R> {
  const actions = IAM_PERMISSIONS[resource] as Record<string, string>;
  return Object.fromEntries(
    Object.entries(actions).map(([action, key]) => [
      `can${action[0]?.toUpperCase()}${action.slice(1)}`,
      granted.has(key),
    ]),
  ) as ResourcePermissions<R>;
}

/** The deny-by-default shape, built from a list of granted permission keys. */
export function buildPermissions(
  permissions: string[] | undefined,
): AllPermissions {
  const granted = new Set(permissions ?? []);
  return Object.fromEntries(
    Object.keys(IAM_PERMISSIONS).map(resource => [
      resource,
      flagsFor(granted, resource as PermissionResource),
    ]),
  ) as AllPermissions;
}

/**
 * What the current user may do, resource by resource — deny-by-default until
 * `GET /api/permissions/me` answers.
 *
 * ```ts
 * const { orders, user } = usePermissions();
 * orders.canCreate;
 * ```
 *
 * No argument: the endpoint answers for every resource in one call, so there
 * is nothing to ask for. Destructure what the component gates on.
 *
 * The resources and their actions come from the shared catalog
 * (`@retracedgmbh/modules/permissions/contracts`), so adding one server-side
 * flows here with no change, and a resource that does not support an action
 * does not offer the flag.
 */
export function usePermissions(): AllPermissions {
  const sessionUser = useSessionStore(s => s.user);
  const { data } = useQuery({
    ...options,
    // Permissions are a property of a session, so there is nothing to ask for
    // without one. Guarding here rather than at the call sites: the login
    // screen builds the sidebar nav (hooks cannot be skipped by an early
    // return), which reaches this hook and fired an unauthenticated request
    // that could only ever 401.
    enabled: Boolean(sessionUser),
  });
  return buildPermissions(data?.data.permissions);
}

/** The same answer in a route's `beforeLoad`, off the same cache entry. */
export async function ensurePermissions(): Promise<AllPermissions> {
  const { data } = await queryClient.ensureQueryData(options);
  return buildPermissions(data.permissions);
}
